/* TOFFEE Set Builder — set-local tier logic and cart synchronization hardening.
 * Tier/progress rules are based only on the products selected in the current set:
 * 1 item = step 1, 2 = step 2, 3–4 = 10%, 5+ = 20%.
 * Existing eligible products elsewhere in the cart never advance this builder's tier preview.
 */
(() => {
  const patchBuilder = () => {
    const Builder = window.customElements?.get('toffee-set-builder');
    if (!Builder || Builder.prototype.__toffeeTierSyncPatched) return;

    const proto = Builder.prototype;
    proto.__toffeeTierSyncPatched = true;

    const originalRender = proto.render;
    const originalAddSelectionToCart = proto.addSelectionToCart;
    const originalEmitCartEvents = proto.emitCartEvents;
    const originalOpenImpactCartDrawer = proto.openImpactCartDrawer;

    if (
      typeof originalRender !== 'function'
      || typeof originalAddSelectionToCart !== 'function'
      || typeof originalOpenImpactCartDrawer !== 'function'
    ) return;

    // The tier belongs to the set currently being configured, never to unrelated
    // eligible products that may already exist in the customer's cart.
    proto.qualifyingQuantity = function qualifyingQuantityFromCurrentSet() {
      if (typeof this.qualifyingSelectionQuantity === 'function') {
        return Math.max(0, Number(this.qualifyingSelectionQuantity()) || 0);
      }
      return Math.max(0, Number(this.totalQuantity?.()) || 0);
    };

    // Explicit thresholds: 0–2 = no discount, 3–4 = 10%, 5+ = 20%.
    proto.discountPercentage = function discountPercentage(quantity = this.qualifyingQuantity()) {
      const normalizedQuantity = Math.max(0, Number(quantity) || 0);
      if (normalizedQuantity >= 5) return 20;
      if (normalizedQuantity >= 3) return 10;
      return 0;
    };

    // Keep the visible explanation aligned with the set-local calculation and
    // suppress the former cart-wide quantity hint, which was misleading here.
    proto.render = function renderSetLocalTier() {
      const result = originalRender.call(this);

      const lead = this.querySelector('.toffee-set-builder__lead');
      if (lead) {
        lead.textContent = 'Wähle deine Favoriten. Deine Rabattstufe richtet sich nur nach den Produkten in diesem Set.';
      }

      this.querySelectorAll('[data-builder-cart-context]').forEach((element) => {
        element.hidden = true;
        element.setAttribute('aria-hidden', 'true');
      });

      const summaryNote = this.querySelector('.toffee-set-builder__summary .toffee-set-builder__step-heading p');
      if (summaryNote) {
        summaryNote.textContent = 'Die Preisvorschau berücksichtigt die Produkte in deinem aktuellen Set.';
      }

      return result;
    };

    // Reconcile the cart immediately before adding a new selection. This remains
    // useful for inventory caps and cart state, but no longer changes the tier.
    proto.addSelectionToCart = async function addSelectionToCartWithFreshCart() {
      if (this.__toffeeTierSyncBusy) return;
      this.__toffeeTierSyncBusy = true;

      try {
        if (!this.editGroupLoaded && typeof this.loadCartEligibleQuantity === 'function') {
          await this.loadCartEligibleQuantity();
          this.render();
        }

        return await originalAddSelectionToCart.call(this);
      } finally {
        this.__toffeeTierSyncBusy = false;
      }
    };

    // Commit the cart returned by Shopify before global cart events are emitted.
    if (typeof originalEmitCartEvents === 'function') {
      proto.emitCartEvents = function emitCartEventsWithFreshState(responseJson, cart) {
        if (cart && typeof this.updateCartEligibleQuantity === 'function') {
          this.updateCartEligibleQuantity(cart);
        }

        return originalEmitCartEvents.call(this, responseJson, cart);
      };
    }

    // Impact refreshes cart sections asynchronously. Give that refresh time to
    // replace old discount markup before the drawer becomes visible.
    proto.openImpactCartDrawer = function openImpactCartDrawerAfterRefresh() {
      window.clearTimeout(this.__toffeeDrawerOpenTimer);
      this.__toffeeDrawerOpenTimer = window.setTimeout(() => {
        originalOpenImpactCartDrawer.call(this);
      }, 420);
    };

    // Existing builder instances may already have rendered before this deferred
    // patch file runs. Re-render once so points, fill and copy are corrected now.
    document.querySelectorAll('toffee-set-builder').forEach((builder) => {
      if (builder instanceof Builder && builder.initialised && typeof builder.render === 'function') {
        builder.render();
      }
    });
  };

  if (window.customElements?.get('toffee-set-builder')) {
    patchBuilder();
  } else if (window.customElements?.whenDefined) {
    window.customElements.whenDefined('toffee-set-builder').then(patchBuilder).catch(() => {});
  }
})();
